import React, { useState, useEffect } from 'react'
import { HiUser, HiLocationMarker, HiCalendar, HiCheckCircle } from 'react-icons/hi'
import { Button, TextInput, Label, Select, Spinner } from 'flowbite-react'
import toast from 'react-hot-toast'
import PlusCode from './PlusCode'
import LocationMap from './LocationMap'
import { OpenLocationCode } from 'open-location-code'

const UserProfileForm = ({ 
  user, 
  onSave, 
  onCancel, 
  isEditing = false, 
  onEditToggle,
  saveTrigger = 0,
  showEditToggle = true,
  showSaveCancel = true,
  showHeader = true,
  title = "User Profile",
  subtitle = "Manage user account information",
  showSystemInfo = true,
  showAccountStatus = true,
  showCoordinates = true
}) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    full_address: '',
    house_number: '',
    road: '',
    address2: '',
    city: '',
    state: '',
    postcode: '',
    country_iso2: 'US',
    mobile_phone: '',
    latitude: null,
    longitude: null,
    plus_code: null,
    timezone: '',
    is_active: true,
    is_superuser: false,
    account_validated: false
  })
  const [countries, setCountries] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [originalLocation, setOriginalLocation] = useState(null)

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters except +
    const phoneNumber = value.replace(/[^\d+]/g, '')
    
    // Handle international format with country code
    if (phoneNumber.startsWith('+1') && phoneNumber.length > 2) {
      const digits = phoneNumber.slice(2) // Remove +1
      if (digits.length >= 10) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
      } else if (digits.length >= 6) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      } else if (digits.length >= 3) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`
      } else if (digits.length > 0) {
        return `+1 (${digits}`
      }
      return '+1'
    }
    
    // Handle 11-digit numbers starting with 1 (US country code without +)
    if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
      const digits = phoneNumber.slice(1) // Remove leading 1
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
    }
    
    // Handle US format without country code (10 digits)
    if (phoneNumber.length >= 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
    } else if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    } else if (phoneNumber.length > 0) {
      return `(${phoneNumber}`
    }
    
    return phoneNumber
  }

  // Fetch countries from API
  const fetchCountries = async () => {
    setCountriesLoading(true)
    try {
      const response = await fetch('https://api.cityseecityfix.com/api/v1/iso2')
      if (response.ok) {
        const data = await response.json()
        setCountries(data)
      } else {
        console.error('Failed to fetch countries')
        toast.error('Failed to load countries list')
      }
    } catch (error) {
      console.error('Error fetching countries:', error)
      toast.error('Failed to load countries list')
    } finally {
      setCountriesLoading(false)
    }
  }

  // Build full address from individual address fields
  const buildFullAddress = (data) => {
    const parts = []
    
    // Add house number and road
    if (data.house_number && data.road) {
      parts.push(`${data.house_number} ${data.road}`)
    } else if (data.house_number) {
      parts.push(data.house_number)
    } else if (data.road) {
      parts.push(data.road)
    }
    
    // Add address2 if present
    if (data.address2) {
      parts.push(data.address2)
    }
    
    // Add city, state, postcode
    const cityStateZip = []
    if (data.city) cityStateZip.push(data.city)
    if (data.state) cityStateZip.push(data.state)
    if (data.postcode) cityStateZip.push(data.postcode)
    
    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '))
    }
    
    // Add country code
    if (data.country_iso2) {
      parts.push(data.country_iso2)
    }
    
    return parts.join(', ')
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    const newData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    }
    
    // Auto-update full_address when address fields change
    if (['house_number', 'road', 'city', 'state', 'postcode', 'country_iso2', 'address2'].includes(name)) {
      newData.full_address = buildFullAddress(newData)
    }
    
    // Auto-update Plus Code when latitude or longitude change
    if (name === 'latitude' || name === 'longitude') {
      const lat = name === 'latitude' ? parseFloat(value) : parseFloat(newData.latitude)
      const lng = name === 'longitude' ? parseFloat(value) : parseFloat(newData.longitude)
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        const plusCode = calculatePlusCode(lat, lng)
        newData.plus_code = plusCode
        
        // Show toast notification when a new Plus Code is generated
        if (plusCode) {
          toast.success(
            <div className="flex items-center space-x-2">
              <img 
                src="/PlusCodesLogo.png" 
                alt="Plus Code" 
                className="w-5 h-5"
              />
              <div>
                <div className="font-medium">Plus Code Changed:</div>
                <div className="font-mono text-sm">{plusCode}</div>
              </div>
            </div>,
            { duration: 4000 }
          )
        }
      }
    }
    
    setFormData(newData)
  }

  // Handle phone number formatting
  const handlePhoneChange = (e) => {
    const formattedValue = formatPhoneNumber(e.target.value)
    const newData = {
      ...formData,
      mobile_phone: formattedValue
    }
    setFormData(newData)
  }

  // Calculate Plus Code from latitude and longitude
  const calculatePlusCode = (lat, lng) => {
    if (OpenLocationCode && lat && lng) {
      try {
        // Use the constructor approach - create instance and call encode method
        const olc = new OpenLocationCode()
        const plusCode = olc.encode(lat, lng, 11) // 11 is extra precision
        return plusCode
      } catch (error) {
        console.error('Error calculating Plus Code:', error)
        return null
      }
    }
    return null
  }

  // Handle location change from map
  const handleLocationChange = (lat, lng) => {
    const plusCode = calculatePlusCode(lat, lng)
    
    // Show toast notification when a new Plus Code is generated
    if (plusCode) {
      toast.success(
        <div className="flex items-center space-x-2">
          <img 
            src="/PlusCodesLogo.png" 
            alt="Plus Code" 
            className="w-5 h-5"
          />
          <div>
            <div className="font-medium">Plus Code Changed:</div>
            <div className="font-mono text-sm">{plusCode}</div>
          </div>
        </div>,
        { duration: 4000 }
      )
    }
    
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString(),
      plus_code: plusCode
    }))
  }

  // Handle address update from reverse geocoding
  const handleAddressUpdate = (addressData) => {
    setFormData(prev => ({
      ...prev,
      house_number: addressData.house_number,
      road: addressData.road,
      address2: addressData.address2,
      city: addressData.city,
      state: addressData.state,
      postcode: addressData.postcode,
      country_iso2: addressData.country_iso2,
      full_address: buildFullAddress({
        ...prev,
        house_number: addressData.house_number,
        road: addressData.road,
        address2: addressData.address2,
        city: addressData.city,
        state: addressData.state,
        postcode: addressData.postcode,
        country_iso2: addressData.country_iso2
      })
    }))
  }

  // Handle save
  const handleSave = async () => {
    setSaving(true)
    try {
      // Convert latitude and longitude to numbers if they exist
      const dataToSave = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        // Ensure boolean fields are properly set
        is_active: Boolean(formData.is_active),
        is_superuser: Boolean(formData.is_superuser),
        account_validated: Boolean(formData.account_validated)
      }
      
      // Remove fields that shouldn't be sent to the API
      delete dataToSave.full_address
      
      // Remove any empty string fields that should be null
      Object.keys(dataToSave).forEach(key => {
        if (dataToSave[key] === '') {
          dataToSave[key] = null
        }
      })
      
      console.log('Form data being saved:', JSON.stringify(dataToSave, null, 2))
      await onSave(dataToSave)
      toast.success('Profile updated successfully!')
    } catch (error) {
      console.error('Error saving profile:', error)
      toast.error('Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      const userData = {
        full_name: user.full_name || '',
        email: user.email || '',
        full_address: user.full_address || '',
        house_number: user.house_number || '',
        road: user.road || '',
        address2: user.address2 || '',
        city: user.city || '',
        state: user.state || '',
        postcode: user.postcode || '',
        country_iso2: user.country_iso2 || 'US',
        mobile_phone: user.mobile_phone || '',
        latitude: user.latitude || null,
        longitude: user.longitude || null,
        plus_code: user.plus_code || null,
        timezone: user.timezone || '',
        is_active: user.is_active !== undefined ? user.is_active : true,
        is_superuser: user.is_superuser || false,
        account_validated: user.account_validated || false
      }
      
      // Generate full address on initial load if coordinates are available
      if (userData.latitude && userData.longitude) {
        userData.full_address = buildFullAddress(userData)
      }
      
      // Don't calculate Plus Code on initial load - only use what's in the database
      // This prevents users from thinking a calculated Plus Code is already saved
      
      setFormData(userData)
      
      // Store original location for distance calculation
      if (userData.latitude && userData.longitude) {
        setOriginalLocation({
          lat: parseFloat(userData.latitude),
          lng: parseFloat(userData.longitude)
        })
      }
    }
  }, [user])

  // Fetch countries on mount
  useEffect(() => {
    fetchCountries()
    
    // OpenLocationCode library is now working correctly
  }, [])

  // Debug countries data for duplicate keys
  useEffect(() => {
    if (countries.length > 0) {
      const iso2Codes = countries.map(c => c.iso2)
      const uniqueIso2Codes = [...new Set(iso2Codes)]
      if (iso2Codes.length !== uniqueIso2Codes.length) {
        console.warn('Duplicate country ISO2 codes found:', iso2Codes.filter((code, index) => iso2Codes.indexOf(code) !== index))
      }
    }
  }, [countries])

  // Respond to external save trigger
  useEffect(() => {
    if (saveTrigger > 0 && isEditing) {
      handleSave()
    }
    // Fires only when the parent bumps saveTrigger. handleSave is recreated
    // every render, so listing it would re-save on every render for as long as
    // saveTrigger > 0. The effect already re-runs on the trigger, so the
    // handleSave it calls is the current one and reads current form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTrigger, isEditing])

  return (
    <div className="space-y-6">
      {/* Header */}
      {showHeader && (
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
              <HiUser className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
              <p className="text-gray-600 dark:text-gray-400">{subtitle}</p>
            </div>
          </div>
          {showEditToggle && !isEditing && (
            <div className="flex space-x-3 ml-4 flex-shrink-0">
              <Button color="blue" onClick={onEditToggle}>
                Edit Profile
              </Button>
            </div>
          )}
          {showSaveCancel && isEditing && (
            <div className="flex space-x-3 ml-4 flex-shrink-0">
              <Button color="gray" onClick={onCancel}>
                Cancel
              </Button>
              <Button color="blue" onClick={handleSave} disabled={saving}>
                {saving ? <Spinner size="sm" className="mr-2" /> : null}
                Save Changes
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Form Content */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <HiUser className="h-5 w-5 mr-2 text-primary-600" />
              Personal Information
            </h3>
            
            <div>
              <Label htmlFor="email">Email Address</Label>
              <TextInput
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
              />
            </div>

            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <TextInput
                id="full_name"
                name="full_name"
                type="text"
                value={formData.full_name}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
              />
            </div>

            <div>
              <Label htmlFor="mobile_phone">Mobile Phone</Label>
              <TextInput
                id="mobile_phone"
                name="mobile_phone"
                type="tel"
                value={formData.mobile_phone}
                onChange={handlePhoneChange}
                disabled={!isEditing}
                placeholder="+1 (555) 123-4567 or (555) 123-4567"
                className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
              />
            </div>

            <div>
              <Label htmlFor="timezone">Timezone</Label>
              <Select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
              >
                <option value="">Select timezone...</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                <option value="UTC">UTC (Coordinated Universal Time)</option>
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Europe/Paris">Paris (CET/CEST)</option>
                <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Shanghai">Shanghai (CST)</option>
                <option value="Asia/Kolkata">Mumbai (IST)</option>
                <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
              </Select>
            </div>
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <HiLocationMarker className="h-5 w-5 mr-2 text-primary-600" />
              Address Information
            </h3>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="full_address">
                  Full Address <span className="text-xs text-gray-500">(Auto-generated)</span>
                </Label>
                <PlusCode 
                  plusCode={formData.plus_code || user?.plus_code} 
                  latitude={formData.latitude}
                  longitude={formData.longitude}
                  onGeneratePlusCode={(lat, lng) => {
                    const plusCode = calculatePlusCode(lat, lng)
                    if (plusCode) {
                      setFormData(prev => ({ ...prev, plus_code: plusCode }))
                      toast.success('Plus Code generated successfully!')
                    } else {
                      toast.error('Failed to generate Plus Code')
                    }
                  }}
                />
              </div>
              <TextInput
                id="full_address"
                name="full_address"
                type="text"
                value={formData.full_address}
                disabled
                className="bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This field is automatically updated when you change the address fields below.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="house_number">House Number</Label>
                <TextInput
                  id="house_number"
                  name="house_number"
                  type="text"
                  value={formData.house_number}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>
              <div>
                <Label htmlFor="road">Road</Label>
                <TextInput
                  id="road"
                  name="road"
                  type="text"
                  value={formData.road}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address2">Address Line 2</Label>
              <TextInput
                id="address2"
                name="address2"
                type="text"
                value={formData.address2}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="Apartment, suite, unit, etc."
                className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="city">City</Label>
                <TextInput
                  id="city"
                  name="city"
                  type="text"
                  value={formData.city}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <TextInput
                  id="state"
                  name="state"
                  type="text"
                  value={formData.state}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>
              <div>
                <Label htmlFor="postcode">Postcode</Label>
                <TextInput
                  id="postcode"
                  name="postcode"
                  type="text"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="country_iso2">Country</Label>
              <Select
                id="country_iso2"
                name="country_iso2"
                value={formData.country_iso2}
                onChange={handleInputChange}
                disabled={!isEditing || countriesLoading}
                className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
              >
                <option value="">Select a country...</option>
                {countries.map((country, index) => (
                  <option key={country.iso2 || `country-${index}`} value={country.iso2}>
                    {country.name} ({country.iso2})
                  </option>
                ))}
              </Select>
              {countriesLoading && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Loading countries...
                </p>
              )}
            </div>

          </div>
        </div>

        {/* Location Coordinates */}
        {showCoordinates && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
              <HiLocationMarker className="h-5 w-5 mr-2 text-primary-600" />
              Location Coordinates
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Coordinates Input */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="latitude">Latitude</Label>
                  <TextInput
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude || ''}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude</Label>
                  <TextInput
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude || ''}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
              </div>

              {/* Map */}
              <LocationMap
                latitude={formData.latitude}
                longitude={formData.longitude}
                onLocationChange={handleLocationChange}
                onAddressUpdate={handleAddressUpdate}
                editable={isEditing}
                height="h-64 lg:h-80"
                originalLocation={originalLocation}
              />
            </div>
          </div>
        )}

        {/* Account Status */}
        {showAccountStatus && (
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
              <HiCheckCircle className="h-5 w-5 mr-2 text-primary-600" />
              Account Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Account Active
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="is_superuser"
                  checked={formData.is_superuser}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Super User
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="account_validated"
                  checked={formData.account_validated}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Account Validated
                </Label>
              </div>
            </div>
          </div>
        )}

        {/* System Information */}
        {showSystemInfo && (
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
              <HiCalendar className="h-5 w-5 mr-2 text-primary-600" />
              System Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="created-at">Created At</Label>
                <TextInput
                  id="created-at"
                  type="text"
                  value={user?.created_at ? new Date(user.created_at).toLocaleString() : 'N/A'}
                  disabled
                  className="bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                />
              </div>
              <div>
                <Label htmlFor="last-login">Last Login</Label>
                <TextInput
                  id="last-login"
                  type="text"
                  value={user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                  disabled
                  className="bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserProfileForm
